import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Send, AlertTriangle, Trash2, Megaphone, ExternalLink, Calendar, Home, LogOut } from 'lucide-react';
import API_URL from '../../config';
import '../Home.css';
import './FacultyHome.css'; // reusing faculty styling

const FacultyAnnouncements = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [activeTab, setActiveTab] = useState("create"); // create | manage
    const [myAnnouncements, setMyAnnouncements] = useState([]);
    const [loadingMy, setLoadingMy] = useState(false);
    
    const [formData, setFormData] = useState({
        message: "",
        link_title: "",
        link_url: "",
        send_to: "all",
        branch: "",
        year: "",
        section: "",
        department: "",
        priority: "normal"
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
        if (!userData.email || (userData.role !== "faculty" && userData.role !== "admin")) {
            navigate("/");
        } else {
            setUser(userData);
        }
    }, [navigate]);

    useEffect(() => {
        if (activeTab === "manage" && user?.email) {
            fetchMyAnnouncements();
        }
    }, [activeTab, user]);

    const fetchMyAnnouncements = async () => {
        setLoadingMy(true);
        try {
            const res = await fetch(`${API_URL}/announcements/manage?email=${user.email}`);
            const data = await res.json();
            if (data.success) {
                setMyAnnouncements(data.announcements);
            }
        } catch (err) {
            console.error("Failed to fetch my announcements", err);
        } finally {
            setLoadingMy(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this announcement?")) return;

        try {
            const res = await fetch(`${API_URL}/announcements/${id}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ requestor_email: user.email, role: user.role })
            });

            if (res.ok) {
                setMyAnnouncements(prev => prev.filter(ann => ann.id !== id));
            } else {
                alert("Failed to delete announcement.");
            }
        } catch (err) {
            console.error("Delete error", err);
            alert("An error occurred.");
        }
    };

    const depts = ["CSE", "CSE-AIML&IOT", "AIDS", "CSBS", "IT", "ECE", "EEE", "EIE", "MECH", "CIVIL", "BIOTECH", "DS", "CYS", "R&AI"];
    const years = ["1st year", "2nd year", "3rd year", "4th year"];
    const sections = ["A", "B", "C", "D"];

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "branch") {
            setFormData(prev => ({
                ...prev,
                branch: value,
                section: (value === "CSE-AIML&IOT" && prev.section === "D") ? "IOT" : prev.section
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.message.trim()) {
            alert("Message is required.");
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = { ...formData, created_by: user.email };
            
            const res = await fetch(`${API_URL}/announcements/create`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                alert("Announcement posted successfully!");
                setActiveTab("manage");
                setFormData({
                    message: "",
                    link_title: "",
                    link_url: "",
                    send_to: "all",
                    branch: "",
                    year: "",
                    section: "",
                    department: "",
                    priority: "normal"
                });
            } else {
                alert("Failed to post announcement.");
            }
        } catch (error) {
            console.error("Error posting announcement:", error);
            alert("An error occurred.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="home-page-container">
            <div className="home-content-wrapper" style={{ maxWidth: '900px', margin: '0 auto' }}>
                <header style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    {/* Back button - left */}
                    <button onClick={() => navigate('/faculty/home')} className="back-btn-modern" style={{ position: 'static', transform: 'none', flexShrink: 0 }}>
                        <ChevronLeft size={20} /> Back
                    </button>
                    {/* Title - centre */}
                    <div style={{ flex: 1, minWidth: '120px' }}>
                        <h1 className="brand-title" style={{ fontSize: '2rem', margin: 0 }}>Announcements</h1>
                        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Broadcast and manage information for the campus.</p>
                    </div>
                    {/* Nav - right */}
                    <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                        <button onClick={() => navigate('/faculty/home')} title="Home" style={{ width: 46, height: 46, borderRadius: '14px', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexShrink: 0 }}><Home size={20} /></button>
                        <button onClick={() => { localStorage.removeItem('vjconnect_user'); navigate('/'); }} title="Logout" style={{ width: 46, height: 46, borderRadius: '14px', border: 'none', background: '#ef4444', color: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(239,68,68,0.4)', flexShrink: 0 }}><LogOut size={20} /></button>
                    </div>
                </header>

                {/* Tabs */}
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
                    {[
                        { id: 'create', label: 'Post New' },
                        { id: 'manage', label: 'My Announcements' }
                    ].map(t => (
                        <button
                            key={t.id}
                            onClick={() => setActiveTab(t.id)}
                            style={{
                                padding: '0.8rem 2rem',
                                borderRadius: '30px',
                                border: '1px solid var(--glass-border)',
                                background: activeTab === t.id ? 'var(--accent-color)' : 'var(--glass-bg)',
                                color: activeTab === t.id ? 'white' : 'var(--text-main)',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                transition: 'all 0.3s'
                            }}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {activeTab === "create" ? (
                    <div className="glass-card" style={{ padding: '2rem' }}>
                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            
                            <div className="profile-field" style={{ gridColumn: '1/-1' }}>
                                <label style={{ fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Announcement Message *</label>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    placeholder="Write your announcement here..."
                                    className="search-input-modern"
                                    style={{ width: '100%', minHeight: '120px', padding: '1rem', resize: 'vertical', marginTop: '0.5rem' }}
                                    required
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div className="profile-field">
                                    <label style={{ fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Link Title (Optional)</label>
                                    <input
                                        name="link_title"
                                        value={formData.link_title}
                                        onChange={handleChange}
                                        placeholder="e.g. View Timetable"
                                        className="search-input-modern"
                                        style={{ width: '100%', padding: '0.8rem', marginTop: '0.5rem' }}
                                    />
                                </div>
                                <div className="profile-field">
                                    <label style={{ fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Link URL (Optional)</label>
                                    <input
                                        name="link_url"
                                        value={formData.link_url}
                                        onChange={handleChange}
                                        placeholder="https://..."
                                        className="search-input-modern"
                                        style={{ width: '100%', padding: '0.8rem', marginTop: '0.5rem' }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div className="profile-field">
                                    <label style={{ fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Target Audience *</label>
                                    <select 
                                        name="send_to" 
                                        value={formData.send_to} 
                                        onChange={handleChange}
                                        className="modern-select"
                                        style={{ width: '100%', marginTop: '0.5rem' }}
                                    >
                                        <option value="all">Everyone</option>
                                        <option value="students">Students</option>
                                        <option value="faculty">Faculty</option>
                                    </select>
                                </div>

                                <div className="profile-field">
                                    <label style={{ fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Priority</label>
                                    <select 
                                        name="priority" 
                                        value={formData.priority} 
                                        onChange={handleChange}
                                        className="modern-select"
                                        style={{ width: '100%', marginTop: '0.5rem', color: formData.priority === 'urgent' ? '#ef4444' : 'inherit' }}
                                    >
                                        <option value="normal">Normal</option>
                                        <option value="urgent">Urgent</option>
                                    </select>
                                </div>
                            </div>

                            {/* Conditional Filters */}
                            {formData.send_to === 'students' && (
                                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', padding: '1rem', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                                    <div className="profile-field" style={{ flex: 1, minWidth: '150px' }}>
                                        <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Branch</label>
                                        <select name="branch" value={formData.branch} onChange={handleChange} className="modern-select" style={{ width: '100%', padding: '0.5rem' }}>
                                            <option value="">All Branches</option>
                                            {depts.map(d => <option key={d} value={d}>{d}</option>)}
                                        </select>
                                    </div>
                                    <div className="profile-field" style={{ flex: 1, minWidth: '150px' }}>
                                        <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Year</label>
                                        <select name="year" value={formData.year} onChange={handleChange} className="modern-select" style={{ width: '100%', padding: '0.5rem' }}>
                                            <option value="">All Years</option>
                                            {years.map(y => <option key={y} value={y}>{y}</option>)}
                                        </select>
                                    </div>
                                    <div className="profile-field" style={{ flex: 1, minWidth: '150px' }}>
                                        <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Section</label>
                                        <select name="section" value={formData.section} onChange={handleChange} className="modern-select" style={{ width: '100%', padding: '0.5rem' }}>
                                            <option value="">All Sections</option>
                                            {(formData.branch === "CSE-AIML&IOT" ? ["A", "B", "C", "IOT"] : sections).map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                </div>
                            )}

                            {formData.send_to === 'faculty' && (
                                <div style={{ padding: '1rem', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                                    <div className="profile-field">
                                        <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Department</label>
                                        <select name="department" value={formData.department} onChange={handleChange} className="modern-select" style={{ width: '100%', padding: '0.5rem' }}>
                                            <option value="">All Departments</option>
                                            {depts.map(d => <option key={d} value={d}>{d}</option>)}
                                        </select>
                                    </div>
                                </div>
                            )}

                            <div style={{ height: '1px', background: 'var(--glass-border)', margin: '1rem 0' }} />

                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <button 
                                    type="submit" 
                                    disabled={isSubmitting}
                                    style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '0.5rem', 
                                        background: 'var(--accent-color)', 
                                        color: 'white', 
                                        border: 'none', 
                                        padding: '0.8rem 2rem', 
                                        borderRadius: '30px', 
                                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                        fontWeight: 'bold',
                                        fontSize: '1rem',
                                        opacity: isSubmitting ? 0.7 : 1
                                    }}
                                >
                                    <Send size={20} />
                                    {isSubmitting ? 'Posting...' : 'Post Announcement'}
                                </button>
                            </div>
                        </form>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {loadingMy ? (
                            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>Loading your announcements...</div>
                        ) : myAnnouncements.length === 0 ? (
                            <div className="glass-card" style={{ textAlign: 'center', padding: '3rem' }}>
                                <Megaphone size={40} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                                <p style={{ color: 'var(--text-secondary)' }}>You haven't posted any announcements yet.</p>
                            </div>
                        ) : (
                            myAnnouncements.map(ann => (
                                <div key={ann.id} className="glass-card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem', borderLeft: ann.priority === 'urgent' ? '4px solid #ef4444' : '1px solid var(--glass-border)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                                                <span style={{ 
                                                    fontSize: '0.7rem', 
                                                    padding: '0.2rem 0.6rem', 
                                                    borderRadius: '10px', 
                                                    background: ann.priority === 'urgent' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255,255,255,0.05)',
                                                    color: ann.priority === 'urgent' ? '#ef4444' : 'var(--text-muted)',
                                                    fontWeight: 'bold',
                                                    textTransform: 'uppercase'
                                                }}>
                                                    {ann.priority}
                                                </span>
                                                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                                    <Calendar size={14} /> {new Date(ann.created_at).toLocaleDateString()}
                                                </span>
                                            </div>
                                            <div style={{ fontSize: '0.85rem', color: 'var(--accent-color)', fontWeight: 'bold' }}>
                                                Target: {ann.send_to === 'all' ? 'Everyone' : `${ann.send_to} (${[ann.branch, ann.year, ann.section, ann.department].filter(Boolean).join(', ') || 'All'})`}
                                            </div>
                                        </div>
                                        <button 
                                            onClick={() => handleDelete(ann.id)}
                                            className="icon-btn"
                                            style={{ color: '#ef4444', background: 'rgba(239, 68, 68, 0.1)', padding: '0.6rem' }}
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                    
                                    <p style={{ margin: 0, fontSize: '1.05rem', lineHeight: '1.5', color: 'var(--text-primary)' }}>{ann.message}</p>
                                    
                                    {ann.link_url && (
                                        <a href={ann.link_url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--accent-color)', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 'bold' }}>
                                            <ExternalLink size={14} /> {ann.link_title || "View Link"}
                                        </a>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FacultyAnnouncements;
